import cx from 'classix';
import { Entry } from 'contentful';
import { BlogArticleSummary, ContentfulRootData } from './types';
import { TypeBlogArticle, TypeBlogCategory, TypePage } from '@platform/contentful';
import {
  getAllSummariesForCategory,
  getArticleSummaries,
  getBlogArticles,
  getBlogCategories,
} from '../blog/util';

export const getFullWidthBackgroundStyles = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entry: Entry<any, 'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>,
) => {
  return cx(
    '-mx-2 sm:-mx-4.5',
    'px-2 py-6 text-center',
    entry.fields.backgroundColor === 'white' && 'bg-white',
    entry.fields.backgroundColor === 'grey' && 'bg-gray-50',
    entry.fields.backgroundColor === 'light-blue' && 'bg-blue-50',
  );
};

export type ContentfulData =
  | {
      type: 'blog-home';
      data: BlogArticleSummary[];
    }
  | {
      type: 'blog-category';
      data: {
        category: TypeBlogCategory<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'> | undefined;
        allCategories: TypeBlogCategory<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>[];
        summaries: BlogArticleSummary[];
      };
    }
  | {
      type: 'blog-article';
      data: TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>;
    }
  | {
      type: 'contentful-page';
      data: TypePage<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>;
    };

/** Inspects a given page route and selects out the specific data required to render it from the total set of
 * Contentful data. This function is used to unify data loading in two very different scenarios:
 * 1. In local dev, when the SPA first loads in localhost as served by Vite, and
 * 2. At app prod build time where we statically generate files to upload into the Azure Storage Account.
 * render-contentful-entries.tsx uses this to load data for all Contentful page and blogArticle entries, renders them
 * initially to flat files, and also bakes the data set returned into the page on the CONTENTFUL_PAGE_DATA
 * global var so it can be retrieved during page hydration & subsequent rendering. */

export const loadContentfulData = (
  route: string,
  contentfulRootData: ContentfulRootData,
): ContentfulData | null => {
  if (route.startsWith('/blog')) {
    const blogArticles = getBlogArticles(contentfulRootData.items);
    const categories = getBlogCategories(blogArticles);
    const summaries = getArticleSummaries(blogArticles);

    // Blog homepage
    if (route === '/blog') {
      return {
        type: 'blog-home',
        data: summaries,
      };
    }

    // Blog category pages
    if (route.startsWith('/blog/category')) {
      const categorySlug = route.slice(15);
      const category = categories.find((c) => c.fields.slug === categorySlug);
      return {
        type: 'blog-category',
        data: {
          category,
          allCategories: categories,
          summaries: getAllSummariesForCategory(summaries, categorySlug),
        },
      };
    }

    const slug = route.slice(6); // Remove "/blog"
    const entry = contentfulRootData.items.find((item) => item.fields.slug === slug);

    return {
      type: 'blog-article',
      data: entry as TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>,
    };
  }

  // Otherwise, should be a route of the form `/:contentful-slug` representing a Contentful 'page' entry
  const slug = route.slice(1);
  const entry = contentfulRootData.items.find((item) => item.fields.slug === slug);

  if (entry?.sys.contentType.sys.id !== 'page') {
    return null;
  }

  return {
    type: 'contentful-page',
    data: entry as TypePage<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>,
  };
};
